<template>
  <div class="sdk-partner-logs-page w-100">
    <Wrapper>
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 class="mb-0">Audit Logs &amp; Usage</h4>
          <div class="text-muted small">{{ partnerName }}</div>
        </div>
        <b-button class="btn btn-light" @click="goBack">
          <i class="fas fa-arrow-left mr-1" />Quay lại
        </b-button>
      </div>

      <!-- Usage panel -->
      <div class="sdk-usage-panel mb-4">
        <div
          v-for="card in usageCards"
          :key="card.key"
          class="sdk-usage-card"
        >
          <div class="sdk-usage-title">{{ card.label }}</div>
          <div class="sdk-usage-value">
            {{ card.used }} <span v-if="!card.noLimit" class="text-muted">/ {{ card.max || '∞' }}</span>
          </div>
          <b-progress v-if="!card.noLimit && card.max > 0" :max="card.max || 1" height="8px" class="mt-2">
            <b-progress-bar
              :value="card.used"
              :variant="card.percent >= 90 ? 'danger' : 'success'"
            />
          </b-progress>
          <div
            v-if="!card.noLimit && card.max > 0 && card.percent >= 90"
            class="text-danger small mt-1"
          >
            <i class="fas fa-exclamation-triangle mr-1" />Gần đạt giới hạn ({{ card.percent }}%)
          </div>
        </div>
      </div>

      <!-- Monthly stats -->
      <div class="mb-4">
        <h6 class="mb-3">Thống kê theo tháng</h6>
        <div v-if="monthlyStats.length" class="table-responsive">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Tháng</th>
                <th>Meetings</th>
                <th>Users</th>
                <th>Webinars</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="m in monthlyStats" :key="m.month">
                <td>{{ m.month }}</td>
                <td>{{ m.meetings }}</td>
                <td>{{ m.users }}</td>
                <td>{{ m.webinars }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="text-muted">Chưa có dữ liệu theo tháng</div>
      </div>

      <!-- Request theo ngày -->
      <div class="mb-4">
        <h6 class="mb-3">Request theo ngày (30 ngày gần nhất)</h6>
        <div v-if="dailyMax > 0">
          <div class="sdk-daychart">
            <div
              v-for="d in dailyRequests"
              :key="d.date"
              class="sdk-daychart-col"
              :title="`${d.date}: ${d.count} request`"
            >
              <div class="sdk-daychart-bar" :style="{ height: barHeight(d.count) }" />
            </div>
          </div>
          <div class="d-flex justify-content-between text-muted small mt-1">
            <span>{{ dailyRequests[0] && dailyRequests[0].date }}</span>
            <span>Cao nhất: {{ dailyMax.toLocaleString('vi-VN') }} / ngày</span>
            <span>{{ dailyRequests[dailyRequests.length - 1] && dailyRequests[dailyRequests.length - 1].date }}</span>
          </div>
        </div>
        <div v-else class="sdk-empty">
          <i class="fas fa-chart-bar" />
          <span>Chưa có request nào trong 30 ngày</span>
        </div>
      </div>

      <!-- Request theo nhóm endpoint -->
      <div class="mb-4">
        <h6 class="mb-3">Request theo nhóm endpoint</h6>
        <div v-if="requestsByEndpoint.length">
          <div v-for="e in requestsByEndpoint" :key="e.endpoint" class="sdk-endpoint-row">
            <div class="sdk-endpoint-name">{{ e.endpoint }}</div>
            <div class="sdk-endpoint-track">
              <div class="sdk-endpoint-fill" :style="{ width: endpointPct(e.count) + '%' }" />
            </div>
            <div class="sdk-endpoint-count">{{ e.count.toLocaleString('vi-VN') }}</div>
          </div>
        </div>
        <div v-else class="sdk-empty">
          <i class="fas fa-stream" />
          <span>Chưa có dữ liệu</span>
        </div>
      </div>

      <!-- Log filters -->
      <b-row>
        <b-col :cols="12" :md="3">
          <b-form-group label="Action">
            <b-form-select v-model="filter.action" :options="actionOptions" @change="applyFilter" />
          </b-form-group>
        </b-col>
        <b-col :cols="12" :md="3">
          <b-form-group label="Platform">
            <b-form-select v-model="filter.platform" :options="platformOptions" @change="applyFilter" />
          </b-form-group>
        </b-col>
        <b-col :cols="12" :md="4">
          <b-form-group label="Khoảng thời gian">
            <date-picker
              v-model="filter.range"
              :range="true"
              input-class="form-control"
              type="date"
              format="DD/MM/YYYY"
              value-type="date"
              @change="applyFilter"
              style="width: 100%"
            />
          </b-form-group>
        </b-col>
        <b-col :cols="12" :md="2">
          <b-form-group label-class="sdk-reset-label" label="&nbsp;">
            <b-button variant="outline-secondary" class="sdk-reset-btn w-100" @click="resetFilter">
              <i class="fas fa-redo mr-1" />Đặt lại
            </b-button>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Logs table -->
      <template-table
        :column="column"
        :data="logs"
        :paging="paging"
        :tableAction="false"
        :selectAction="false"
        :searchAction="false"
      >
        <template v-slot:body="{ item }">
          <td>{{ formatDateTime(item.createdAt) }}</td>
          <td>{{ item.userId }}</td>
          <td><b-badge variant="light">{{ item.action }}</b-badge></td>
          <td>{{ item.platform }}</td>
          <td>{{ item.sdkVersion }}</td>
          <td>{{ item.durationMs }}</td>
          <td>{{ item.ipAddress }}</td>
        </template>
      </template-table>
    </Wrapper>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import SdkPartnerService, { LOG_ACTIONS } from './services/sdk-partner.service';

export default {
  name: 'sdk_partner_logs',
  components: {
    'date-picker': DatePicker,
  },
  data() {
    return {
      partnerId: this.$route.params.id,
      partnerName: this.$route.query.name || this.$route.params.id,
      usage: null,
      logs: [],
      paging: { page: 1, pageSize: 20, total: 0 },
      filter: { action: null, platform: null, range: null },
      actionOptions: [
        { value: null, text: 'Tất cả' },
        ...LOG_ACTIONS.map((a) => ({ value: a, text: a })),
      ],
      platformOptions: [
        { value: null, text: 'Tất cả' },
        { value: 'ios', text: 'iOS' },
        { value: 'android', text: 'Android' },
      ],
      column: [
        { key: 'createdAt', label: 'Thời gian' },
        { key: 'userId', label: 'User ID' },
        { key: 'action', label: 'Action' },
        { key: 'platform', label: 'Platform' },
        { key: 'sdkVersion', label: 'SDK Version' },
        { key: 'durationMs', label: 'Duration (ms)' },
        { key: 'ipAddress', label: 'IP Address' },
      ],
    };
  },
  computed: {
    monthlyStats() {
      return (this.usage && this.usage.monthlyStats) || [];
    },
    dailyRequests() {
      return (this.usage && this.usage.dailyRequests) || [];
    },
    dailyMax() {
      return this.dailyRequests.reduce((m, d) => Math.max(m, d.count || 0), 0);
    },
    requestsByEndpoint() {
      return (this.usage && this.usage.requestsByEndpoint) || [];
    },
    endpointMax() {
      return this.requestsByEndpoint.reduce((m, e) => Math.max(m, e.count || 0), 0);
    },
    usageCards() {
      const u = this.usage || {};
      const pct = (used, max) =>
        max ? Math.min(100, Math.round(((used || 0) / max) * 100)) : 0;
      return [
        {
          key: 'meetings',
          label: 'Meetings (tháng này)',
          used: u.meetingsUsed || 0,
          max: u.maxMeetings || 0,
          percent: pct(u.meetingsUsed, u.maxMeetings),
        },
        {
          key: 'users',
          label: 'Current Users',
          used: u.currentUsers || 0,
          max: u.maxUsers || 0,
          percent: pct(u.currentUsers, u.maxUsers),
        },
        {
          key: 'webinars',
          label: 'Webinars (tháng này)',
          used: u.webinarsUsed || 0,
          max: u.maxWebinars || 0,
          percent: pct(u.webinarsUsed, u.maxWebinars),
        },
        {
          key: 'requests',
          label: 'Tổng số request',
          used: (u.totalRequests || 0).toLocaleString('vi-VN'),
          max: 0,
          percent: 0,
          noLimit: true,
        },
      ];
    },
  },
  watch: {
    'paging.page'() {
      this.loadLogs();
    },
    'paging.pageSize'() {
      this.paging.page = 1;
      this.loadLogs();
    },
  },
  created() {
    if (!SdkPartnerService.isConfigured()) {
      this.$toastr.e({
        title: 'Lỗi !',
        msg: 'Chưa cấu hình VUE_APP_SDK_GATEWAY',
        preventDuplicates: true,
      });
      return;
    }
    this.loadUsage();
    this.loadLogs();
  },
  methods: {
    barHeight(count) {
      if (!count || !this.dailyMax) return '0%';
      return `${Math.max(6, Math.round((count / this.dailyMax) * 100))}%`;
    },
    endpointPct(count) {
      if (!count || !this.endpointMax) return 0;
      return Math.max(2, Math.round((count / this.endpointMax) * 100));
    },
    async loadUsage() {
      try {
        const res = await SdkPartnerService.getUsage(this.partnerId);
        this.usage = res && res.data ? res.data : res;
      } catch (error) {
        this.toastError(error);
      }
    },
    async loadLogs() {
      this.$store.commit('context/setLoading', true);
      try {
        const params = {
          page: this.paging.page,
          pageSize: this.paging.pageSize,
        };
        if (this.filter.action) params.action = this.filter.action;
        if (this.filter.platform) params.platform = this.filter.platform;
        if (this.filter.range && this.filter.range[0]) {
          params.from = this.$moment(this.filter.range[0]).toISOString();
        }
        if (this.filter.range && this.filter.range[1]) {
          params.to = this.$moment(this.filter.range[1]).toISOString();
        }
        const res = await SdkPartnerService.getLogs(this.partnerId, params);
        this.logs = (res && res.data) || [];
        this.paging.total = (res && res.total) || 0;
      } catch (error) {
        this.toastError(error);
      } finally {
        this.$store.commit('context/setLoading', false);
      }
    },
    applyFilter() {
      this.paging.page = 1;
      this.loadLogs();
    },
    resetFilter() {
      this.filter = { action: null, platform: null, range: null };
      this.paging.page = 1;
      this.loadLogs();
    },
    formatDateTime(date) {
      if (!date) return '—';
      const m = this.$moment(date);
      return m.isValid() ? m.format('DD/MM/YYYY HH:mm:ss') : '—';
    },
    goBack() {
      this.$router.push({ name: 'sdk_partner' });
    },
    toastError(error) {
      this.$toastr.e({
        title: 'Lỗi !',
        msg: (error && error.message) || 'Đã có lỗi xảy ra',
        preventDuplicates: true,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.sdk-usage-panel {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.sdk-daychart {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 90px;
  padding: 6px;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  background: #f8f9fc;
}
.sdk-daychart-col {
  flex: 1;
  height: 100%;
  display: flex;
  align-items: flex-end;
}
.sdk-daychart-bar {
  width: 100%;
  background: #8950fc;
  border-radius: 2px;
}
.sdk-endpoint-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}
.sdk-endpoint-name {
  width: 120px;
  font-weight: 600;
  text-transform: capitalize;
}
.sdk-endpoint-track {
  flex: 1;
  height: 10px;
  background: #ebedf3;
  border-radius: 5px;
  overflow: hidden;
}
.sdk-endpoint-fill {
  height: 100%;
  background: #0bb783;
  border-radius: 5px;
}
.sdk-endpoint-count {
  width: 64px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/* Friendly empty state (chart / breakdown with no data) */
.sdk-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 96px;
  padding: 22px;
  color: #a1a5b7;
  border: 1px dashed #d7dae3;
  border-radius: 10px;
  background: #fafbfd;
  font-size: 13px;
}
.sdk-empty i {
  font-size: 22px;
  opacity: 0.6;
}

/* Reset filter: quiet secondary action, not a warning */
.sdk-reset-btn {
  font-weight: 500;
  height: calc(1.5em + 1.3rem + 2px); /* match form-control height for row alignment */
}

.sdk-usage-card {
  padding: 16px;
  border: 1px solid #e4e6ef;
  border-radius: 10px;
  background: #fff;

  .sdk-usage-title {
    font-size: 13px;
    color: #7e8299;
  }

  .sdk-usage-value {
    font-size: 22px;
    font-weight: 700;
    margin-top: 4px;
  }
}
</style>
